import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Table, Label, Message, Button, Icon, Grid, Card } from 'semantic-ui-react';
import { BodyText, ControlContainer, H2, H3, H4, PageHeader, PageSection } from 'common/styles/page';
import { authenticatedFetch } from 'common/utils/authFetch';
import { FieldItem, FieldLabel, FieldList, FieldValue, Subtitle } from '../styles/styles';

interface Process {
  hostname: string;
  process_type: 'worker' | 'beat';
  status: 'active' | 'inactive';
  last_heartbeat: string | null;
  heartbeat_key: string;
}

interface WorkerStatusData {
  summary: {
    total_processes: number;
    total_active: number;
    total_inactive: number;
    workers: {
      total: number;
      active: number;
      inactive: number;
    };
    beat: {
      total: number;
      active: number;
      inactive: number;
    };
    current_server: string;
  };
  processes: Process[];
  heartbeat_config: {
    expiration_seconds: number;
    recommended_interval_seconds: number;
    cleanup_interval_minutes: number;
  };
  monitoring_info: {
    heartbeat_task: string;
    cleanup_task: string;
    description: string;
  };
}

const WorkerMonitoring: React.FC = () => {
  const [workerData, setWorkerData] = useState<WorkerStatusData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchWorkerStatus = async () => {
    // Prevent multiple simultaneous requests
    if (loading) return;
    
    setLoading(true);
    setError(null);
    try {
      // Use the configured API base URL instead of relative URL
      const apiBaseUrl = process.env.NODE_ENV === 'development' ? '' : process.env.REACT_APP_API_URL;
      const response = await authenticatedFetch(`${apiBaseUrl}/api/admin/v1/system-health/worker_status/`, {
        headers: { 'Content-Type': 'application/json' },
        // Add timeout to prevent hanging requests
        signal: AbortSignal.timeout(10000), // 10 second timeout
      });

      if (response.ok) {
        const data = await response.json();
        setWorkerData(data);
        setLastRefresh(new Date());
      } else {
        setError('Failed to fetch worker status');
      }
    } catch (error) {
      setError('Failed to fetch worker status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkerStatus();
    // Set up auto-refresh every 60 seconds (reduced from 30 seconds)
    const interval = setInterval(fetchWorkerStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => (status === 'active' ? 'green' : 'red');

  const getStatusIcon = (status: string) => (status === 'active' ? 'check circle' : 'times circle');

  const formatHeartbeatTime = (heartbeatTime: string | null) => {
    if (!heartbeatTime) return 'Never';
    try {
      // Handle different timestamp formats
      let date: Date;

      // Check if it's a Unix timestamp (seconds since epoch)
      if (/^\d+$/.test(heartbeatTime)) {
        date = new Date(parseInt(heartbeatTime) * 1000);
      } else {
        // Try parsing as ISO string or other date format
        date = new Date(heartbeatTime);
      }

      // Validate the date
      if (isNaN(date.getTime())) {
        console.warn('Invalid heartbeat time for formatting:', heartbeatTime);
        return 'Invalid time';
      }

      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting heartbeat time:', error, heartbeatTime);
      return 'Invalid time';
    }
  };

  const getTimeSinceHeartbeat = (heartbeatTime: string | null) => {
    if (!heartbeatTime) return 'Unknown';
    try {
      let heartbeat: Date;
      // Always parse as UTC: if missing 'Z', add it
      let normalized = heartbeatTime;
      if (!/Z$/.test(heartbeatTime) && !/([+-]\d{2}:?\d{2})$/.test(heartbeatTime)) {
        normalized = heartbeatTime + 'Z';
      }
      heartbeat = new Date(normalized);
      if (isNaN(heartbeat.getTime())) {
        console.warn('Invalid heartbeat time:', heartbeatTime);
        return 'Invalid time';
      }
      // Use Date.now() for current UTC time
      const nowUtcMs = Date.now();
      const diffMs = nowUtcMs - heartbeat.getTime();
      const diffSeconds = Math.floor(diffMs / 1000);
      if (diffSeconds < 0) {
        return '0 seconds ago';
      }
      if (diffSeconds < 60) {
        return `${diffSeconds} seconds ago`;
      } else if (diffSeconds < 3600) {
        const minutes = Math.floor(diffSeconds / 60);
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      } else {
        const hours = Math.floor(diffSeconds / 3600);
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      }
    } catch (error) {
      console.error('Error calculating time since heartbeat:', error, heartbeatTime);
      return 'Invalid time';
    }
  };

  const tableData =
    workerData?.processes.map(process => ({
      key: `${process.process_type}-${process.hostname}`,
      hostname: process.hostname,
      processType: process.process_type.charAt(0).toUpperCase() + process.process_type.slice(1),
      status: (
        <Label color={getStatusColor(process.status) as any}>
          <Icon name={getStatusIcon(process.status)} />
          {process.status === 'active' ? 'Active' : 'Inactive'}
        </Label>
      ),
      lastHeartbeat: formatHeartbeatTime(process.last_heartbeat),
      timeSince: getTimeSinceHeartbeat(process.last_heartbeat),
      isCurrentServer: process.hostname === workerData?.summary.current_server,
    })) || [];

  return (
    <PageSection>
      {error && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      <PageSection display='flex' padbottom justifyContent='space-between' alignItems='center'>
      <PageHeader title="Celery Worker Status" 
        controls={  
          <ControlContainer>
          {lastRefresh && (
            <LastUpdatedText>
              Last updated: {lastRefresh.toLocaleTimeString()}
            </LastUpdatedText>
          )}
          <Button
            icon
            title="Refresh"
            aria-label="Refresh"
            disabled={loading}
            onClick={fetchWorkerStatus}
            loading={loading}
            size="small"
          >
            <Icon name="refresh" />
          </Button>
        </ControlContainer>
        }
      
      />

      </PageSection>

      {workerData && (
        <>
          <div style={statsGrid5}>
            <div style={gridItem}>
              <Card fluid raised style={cardStyle}>
                <H2>Total Processes</H2>
                <div style={value('#333')}>
                  {workerData.summary.total_processes.toLocaleString()}
                </div>
                <BodyText>All Workers + Beat</BodyText>
              </Card>
            </div>

            <div style={gridItem}>
              <Card fluid raised style={cardStyle}>
                <H2>Active Processes</H2>
                <div style={value('#21ba45')}>
                  {workerData.summary.total_active.toLocaleString()}
                </div>
                <BodyText>Running</BodyText>
              </Card>
            </div>

            <div style={gridItem}>
              <Card fluid raised style={cardStyle}>
                <H2>Inactive Processes</H2>
                <div style={value('#db2828')}>
                  {workerData.summary.total_inactive.toLocaleString()}
                </div>
                <BodyText>Not Running</BodyText>
              </Card>
            </div>

            <div style={gridItem}>
              <Card fluid raised style={cardStyle}>
                <H2>Active Workers</H2>
                <div style={value('#1976d2')}>
                  {workerData.summary.workers.active.toLocaleString()}
                </div>
                <BodyText>Worker Processes</BodyText>
              </Card>
            </div>

            <div style={gridItem}>
              <Card fluid raised style={cardStyle}>
                <H2>Active Beat</H2>
                <div style={value('#f57c00')}>
                  {workerData.summary.beat.active.toLocaleString()}
                </div>
                <BodyText>Scheduler</BodyText>
              </Card>
            </div>
          </div>

<PageSection padbottom>
  <Card fluid raised style={{ padding: '20px' }}>
    <Grid columns={2} stackable divided>
      <Grid.Row>
        {/* Left column */}
        <Grid.Column>
          <H2 padbottom>Heartbeat Settings</H2>
          <FieldList>
            <FieldItem>
              <FieldLabel>Expiration</FieldLabel>
              <FieldValue>{workerData.heartbeat_config.expiration_seconds} seconds</FieldValue>
            </FieldItem>
            <FieldItem>
              <FieldLabel>Recommended Interval</FieldLabel>
              <FieldValue>{workerData.heartbeat_config.recommended_interval_seconds} seconds</FieldValue>
            </FieldItem>
            <FieldItem>
              <FieldLabel>Cleanup Interval</FieldLabel>
              <FieldValue>{workerData.heartbeat_config.cleanup_interval_minutes} minutes</FieldValue>
            </FieldItem>
            <FieldItem>
              <FieldLabel>Current Server</FieldLabel>
              <FieldValue>{workerData.summary.current_server}</FieldValue>
            </FieldItem>
          </FieldList>
        </Grid.Column>

        {/* Right column */}
        <Grid.Column>
          <H2 padbottom>Tasks</H2>
          <FieldList>
            <FieldItem>
              <FieldLabel>Heartbeat Task</FieldLabel>
              <FieldValue>{workerData.monitoring_info.heartbeat_task}</FieldValue>
            </FieldItem>
            <FieldItem>
              <FieldLabel>Cleanup Task</FieldLabel>
              <FieldValue>{workerData.monitoring_info.cleanup_task}</FieldValue>
            </FieldItem>
            <FieldItem>
              <FieldLabel>Description</FieldLabel>
              <FieldValue>{workerData.monitoring_info.description}</FieldValue>
            </FieldItem>
          </FieldList>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  </Card>
</PageSection>

          {workerData.processes.length !== 0 ? (

            <Table celled selectable loading={loading} compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Hostname</Table.HeaderCell>
                  <Table.HeaderCell>Process Type</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Last Heartbeat</Table.HeaderCell>
                  <Table.HeaderCell>Time Since</Table.HeaderCell>
                  <Table.HeaderCell>Server</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {tableData.map(row => (
                  <Table.Row key={row.key}>
                    <Table.Cell>
                      <strong>{row.hostname}</strong>
                    </Table.Cell>
                    <Table.Cell>{row.processType}</Table.Cell>
                    <Table.Cell>{row.status}</Table.Cell>
                    <Table.Cell>{row.lastHeartbeat}</Table.Cell>
                    <Table.Cell>{row.timeSince}</Table.Cell>
                    <Table.Cell>
                      {row.isCurrentServer ? (
                        <Label color="blue" size="tiny">
                          Current
                        </Label>
                      ) : (
                        '-'
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <PageSection padbottom>
              <Message info>
                <Message.Header>No Processes Detected</Message.Header>
                <p>No process heartbeats have been received. Make sure:</p>
                <ul>
                  <li>Celery workers are running</li>
                  <li>The heartbeat task is scheduled and enabled</li>
                  <li>Workers have been restarted after setting up monitoring</li>
                  <li>Redis is accessible and configured correctly</li>
                </ul>
              </Message>
            </PageSection>
          )}
        </>
      )}
    </PageSection>
  );
};


export default WorkerMonitoring;


const LastUpdatedText = styled(BodyText)`
  white-space: nowrap;
  margin-right: 8px;
`;

const statsGrid5 = {
  display: 'grid',
  gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
  gap: '10px',
  alignItems: 'stretch',
  marginBottom: '24px',
} as const;

const gridItem = { minWidth: 0 } as const;

const cardStyle = {
  margin: 0,
  width: '100%',
  height: '100%',
  border: '1px solid #d4d4d5',
  borderRadius: '6px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  padding: '12px',
} as const;

const value = (color = '#333') =>
  ({ fontSize: '24px', fontWeight: 'bold', color } as const);


